import { z } from 'zod';

// ─── Size-param axis (3-axis caps model) ───────────────────────────────────

/** A selectable value for the resolution-tier axis. `long_edge` is the target
 *  long-edge in px used ONLY by size-native (pixel) models (GPT) during the
 *  ratio→pixel conversion; aspect-native models (NB) send `key` as `resolution`. */
export const imageCapsSizeValueSchema = z.object({
  key: z.string(),                                   // '1K' | '2K' | '4K'
  label: z.string(),                                 // '1K' | '2K' | '4K'
  long_edge: z.number().int().positive().optional(), // GPT tier long-edge: 720 | 1280 | 1792
});

/** Pixel-grid constraints for `pixel_compute` models (GPT). fal-tested baseline
 *  (Amendment A2) seeded in migration 007; discovery overlays min/max edge from
 *  the OpenAPI schema where present (it exposes only a loose >0 / ≤14142 — NOT
 *  multipleOf or total-px; Amendment A1), seed-preserved otherwise. The
 *  conversion helper reads these instead of hardcoding the grid. Omitted for
 *  aspect_native models (NB doesn't compute pixels). */
export const imageSizeConstraintsSchema = z.object({
  min_edge: z.number().int().positive(),       // floor on each edge — 16 (OpenAPI only says >0)
  max_edge: z.number().int().positive(),       // fal-tested /edit cap — 1792 (NOT the loose 14142 OpenAPI max)
  step: z.number().int().positive(),           // multipleOf grid — 16 (OpenAPI omits it; seed-sourced)
  // Pixel-area bounds do NOT apply to /edit (A2) → left UNSET for GPT; optional so a
  // future model that needs them can set them and the helper's inert guard activates.
  min_total_px: z.number().int().positive().optional(),
  max_total_px: z.number().int().positive().optional(),
  max_ratio: z.number().positive().optional(),          // long/short cap — unset for GPT (no ratio limit observed)
});

/** Resolution-tier axis. `mode` tells the worker how to apply the chosen tier:
 *  - 'aspect_native'  → send `key` verbatim on the model's `resolution` field (NB).
 *  - 'pixel_compute'  → convert (ratio, tier.long_edge, constraints) → image_size {w,h} (GPT). */
export const imageCapsSizeParamSchema = z.object({
  field: z.literal('resolution'),       // the provider field this maps to (NB) / drives compute (GPT)
  label: z.string(),                    // UI label ('Size')
  mode: z.enum(['aspect_native', 'pixel_compute']),
  values: z.array(imageCapsSizeValueSchema).min(1),
  default: z.string(),
  /** Required when mode==='pixel_compute'; absent for aspect_native. Enforce the
   *  conditional with a zod .superRefine on imageCapsSizeParamSchema. */
  constraints: imageSizeConstraintsSchema.optional(),
}).superRefine((val, ctx) => {
  if (val.mode === 'pixel_compute' && !val.constraints) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'size_param with mode="pixel_compute" must have constraints defined',
      path: ['constraints'],
    });
  }
});

export type ImageCapsSizeValue = z.infer<typeof imageCapsSizeValueSchema>;
export type ImageSizeConstraints = z.infer<typeof imageSizeConstraintsSchema>;
export type ImageCapsSizeParam = z.infer<typeof imageCapsSizeParamSchema>;

export const moodKeySchema = z.enum([
  'cozy', 'serene', 'dramatic', 'playful', 'sophisticated', 'romantic', 'energetic',
]);
export const lightingKeySchema = z.enum([
  // v1 — Window light
  'morning_sunlight', 'golden_hour', 'soft_daylight', 'evening_warm_glow', 'overcast',
  // v2 — Bright / Studio
  'studio_softbox', 'bright_airy', 'high_key', 'showroom_bright',
]);
export const styleEraKeySchema = z.enum([
  'scandi', 'japandi', 'mid_century', 'industrial', 'boho', 'minimalist', 'traditional', 'art_deco',
]);
export const cameraAngleKeySchema = z.enum([
  'frontal', 'left_3_4', 'right_3_4', 'wide',
]);

export const aiInteriorGenerateRequestedSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  room_category_id: z.number().int().positive(),
  count: z.number().int().min(1).max(4).default(1),
  prompt_id_override: z.number().int().positive().optional(),
  model_id_override: z.number().int().positive().optional(),
  /** 1 = pristine showroom, 10 = real lived-in chaos. Default 5. */
  chaos: z.number().int().min(1).max(10).optional(),
  mood: moodKeySchema.optional(),
  lighting: lightingKeySchema.optional(),
  style_era: styleEraKeySchema.optional(),
  camera_angle: cameraAngleKeySchema.optional(),
  /**
   * Per-model generation params (Tab 3). Open strings here — the allowed set is
   * MODEL-SPECIFIC (driven by ai_models.image_caps), so it can't be a fixed
   * z.enum. The worker validates membership against the resolved model's
   * `image_caps` before the provider call (needs-sec-pass: closed-enum vs caps).
   * - aspect_ratio: ratio key (e.g. '16:9')
   * - size_param_value: resolution tier key (e.g. '2K') — validated against caps.size_param
   * - cost_param_value: the generic cost-driving value (quality)
   */
  aspect_ratio: z.string().optional(),
  /** Resolution tier key (e.g. '2K') — validated against caps.size_param. */
  size_param_value: z.string().optional(),
  cost_param_value: z.string().optional(),
  /**
   * Pre-composed creative scene block. When present (retry path), the handler
   * reuses it verbatim and skips the creative LLM stage.
   */
  creative_block: z.string().optional(),
});

export const wallpapersInteriorDeleteRequestedSchema = z.object({
  interior_id: z.number().int().positive(),
});

export type AiInteriorGenerateRequestedPayload = z.infer<
  typeof aiInteriorGenerateRequestedSchema
>;
export type WallpapersInteriorDeleteRequestedPayload = z.infer<
  typeof wallpapersInteriorDeleteRequestedSchema
>;
export type CameraAngleSchemaKey = z.infer<typeof cameraAngleKeySchema>;

// ─── Interior upscale (fal clarity/aura-sr/recraft-crisp) ──────────────────
// SoT: idea 2026-07-04-wandero-interior-upscale-fal-clarity, spec §"upscale_caps
// (shared SoT)". Structural sibling of imageCapsSchema, stored on
// `ai_models.upscale_caps` (JSONB, no DB CHECK — shared is SoT, same pattern
// as image_caps).

export const upscaleCapsScaleSchema = z
  .object({
    /** fal body field the resolved scale value is sent on — null for 'none' mode. */
    param_field: z.string().nullable(),
    mode: z.enum(['slider', 'fixed', 'none']),
    /** Slider snap points (e.g. [1.5, 2, 4]) — required when mode==='slider'. */
    values: z.array(z.number().positive()).optional(),
    /** Fixed multiplier (e.g. 4) — required when mode==='fixed'. */
    fixed: z.number().positive().optional(),
    /** Slider default — one of `values`. */
    default: z.number().positive().optional(),
    /** Whether the resolved scale value is sent as a number or a string
     *  (e.g. aura-sr's `upscaling_factor` is an enum string "4"). */
    value_type: z.enum(['number', 'string']),
  })
  .superRefine((val, ctx) => {
    if (val.mode === 'slider' && (!val.values || val.values.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'scale with mode="slider" must have a non-empty values array',
        path: ['values'],
      });
    }
    if (val.mode === 'fixed' && val.fixed === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'scale with mode="fixed" must have fixed defined',
        path: ['fixed'],
      });
    }
  });

export const upscaleCapsSchema = z.object({
  scale: upscaleCapsScaleSchema,
  /** Extra fal params always sent verbatim (e.g. clarity's creativity/resemblance). */
  fixed_params: z.record(z.union([z.number(), z.string(), z.boolean()])).optional(),
  /** Non-empty prompt sent to fal (R1: FalHandler requires task.prompt on ALL
   *  models, including prompt-less upscalers). Falls back to 'upscale' at the
   *  call site when absent. */
  prompt: z.string().optional(),
  pricing: z.object({
    unit: z.enum(['image', 'megapixel']),
    mode: z.enum(['flat', 'range']),
    flat: z.number().nonnegative().optional(),
    range: z.object({ min: z.number().nonnegative(), max: z.number().nonnegative() }).optional(),
  }),
  /** ISO timestamp of when caps/pricing were last pulled from fal. */
  fetched_at: z.string(),
});

export type UpscaleCapsScale = z.infer<typeof upscaleCapsScaleSchema>;
export type UpscaleCaps = z.infer<typeof upscaleCapsSchema>;

export const aiInteriorUpscaleRequestedSchema = z.object({
  interior_id: z.number().int().positive(),
  model_id: z.number().int().positive(),
  /** Requested scale (slider mode only) — ignored for fixed/none per resolveUpscaleScale semantics. */
  scale: z.number().positive().optional(),
});

export type AiInteriorUpscaleRequestedPayload = z.infer<
  typeof aiInteriorUpscaleRequestedSchema
>;

/**
 * Build the fal `fal_params` object for an upscale run from caps + a resolved
 * scale value. `scale` is the value already resolved by the caller (worker
 * picks it per `caps.scale.mode` — slider input vs fixed vs none); this
 * helper only handles the param_field + value_type mapping and merges
 * `fixed_params` last (fixed_params never overridden by the scale field).
 */
export function buildUpscaleFalParams(
  caps: UpscaleCaps,
  scale?: number,
): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  const { param_field, value_type } = caps.scale;
  if (param_field && scale !== undefined) {
    params[param_field] = value_type === 'string' ? String(scale) : scale;
  }
  return { ...params, ...(caps.fixed_params ?? {}) };
}

/**
 * Estimate the USD cost of an upscale run from caps pricing + the target
 * (post-upscale) dimensions. `unit==='megapixel'` → rate × output megapixels
 * (rounding up handled by the caller/UI, this returns the raw product);
 * `unit==='image'` → flat/range same as estimateImageCostFromCaps. Returns
 * null when pricing carries neither flat nor range.
 */
export function estimateUpscaleCost(
  caps: UpscaleCaps,
  { width, height, scale }: { width: number; height: number; scale: number },
): number | null {
  const { pricing } = caps;
  const rate =
    pricing.mode === 'flat' && typeof pricing.flat === 'number'
      ? pricing.flat
      : pricing.mode === 'range' && pricing.range
        ? (pricing.range.min + pricing.range.max) / 2
        : typeof pricing.flat === 'number'
          ? pricing.flat
          : pricing.range
            ? (pricing.range.min + pricing.range.max) / 2
            : null;
  if (rate === null) return null;
  if (pricing.unit === 'megapixel') {
    const outMp = (width * scale * (height * scale)) / 1_000_000;
    return rate * outMp;
  }
  return rate;
}

/** Structural row shape resolveUpscaleServedPath needs — NOT db-types-coupled
 *  (both `wallpapers_interiors` rows and any future fixture can satisfy it). */
export interface InteriorServedPathRow {
  storage_path: string;
  upscaled_path: string | null;
  upscale_active: boolean;
}

/**
 * Resolve the currently-served storage path for an interior: the upscaled
 * variant when active and present, otherwise the (always-preserved) original.
 */
export function interiorServedPath(row: InteriorServedPathRow): string {
  return row.upscale_active && row.upscaled_path ? row.upscaled_path : row.storage_path;
}

// ─── Per-model image capability + pricing descriptor (ai_models.image_caps) ────
// Auto-filled from the fal model schema + /pricing/estimate at discovery time
// (worker), optional manual override. Drives Tab 3 of the interior settings
// popover. Universal across providers: the "cost-driving" axis is a generic
// `cost_param` (quality for GPT Image, resolution for Nano Banana 2 Edit / Pro,
// omitted for models without one). See idea
// 2026-06-30-wandero-interior-gen-size-ratio-quality-pricing.

/** A selectable value for the ratio axis (size preset or aspect ratio). */
export const imageCapsRatioValueSchema = z.object({
  key: z.string(),
  label: z.string(),
  w: z.number().int().positive().optional(),
  h: z.number().int().positive().optional(),
  ratio: z.string().optional(),
});

/** A selectable value for the generic cost axis (quality / resolution). */
export const imageCapsCostValueSchema = z.object({
  key: z.string(),
  label: z.string(),
});

export const imageCapsSchema = z.object({
  /** How aspect ratio / size is set on this model. */
  ratio_param: z.object({
    field: z.enum(['image_size', 'aspect_ratio']),
    values: z.array(imageCapsRatioValueSchema).min(1),
    default: z.string(),
  }),
  /** Resolution-tier axis (1K/2K/4K). New 3-axis model — optional so legacy models still parse. */
  size_param: imageCapsSizeParamSchema.optional(),
  /** Generic cost-driving param — omitted for models without one. */
  cost_param: z
    .object({
      field: z.string(), // 'quality' | …
      label: z.string(), // UI label ('Quality')
      values: z.array(imageCapsCostValueSchema).min(1),
      default: z.string(),
    })
    .optional(),
  pricing: z.object({
    unit: z.enum(['image', 'megapixel']),
    mode: z.enum(['flat', 'range']),
    /** Exact unit price — for models without a cost axis. */
    flat: z.number().nonnegative().optional(),
    /** Price range — for tiered models (estimate can't expand the full matrix). */
    range: z.object({ min: z.number().nonnegative(), max: z.number().nonnegative() }).optional(),
  }),
  /** ISO timestamp of when caps/pricing were last pulled from fal. */
  fetched_at: z.string(),
});

export type ImageCaps = z.infer<typeof imageCapsSchema>;
export type ImageCapsRatioValue = z.infer<typeof imageCapsRatioValueSchema>;
export type ImageCapsCostValue = z.infer<typeof imageCapsCostValueSchema>;

// ─── Per-model gen-param validation (needs-sec-pass: closed enum vs caps) ──────
// The event payload carries `aspect_ratio` / `cost_param_value` as OPEN strings
// (the allowed set is per-model). Before the provider call the worker must
// reject any value not present in the resolved model's `image_caps`, so only
// caps-enumerated values ever reach fal. These helpers centralise that gate so
// it's unit-testable. Both default to the caps default when the input is
// undefined (settings not yet chosen), and return an error when a value is
// present but not a member of the caps enum.

export interface ResolvedGenParams {
  /** Validated ratio value (a key from ratio_param.values). */
  aspect_ratio: string;
  /** Validated size tier key (a key from size_param.values) — undefined if the
   *  model has no size axis. */
  size_param_value: string | undefined;
  /** Validated cost value (a key from cost_param.values) — undefined if the
   *  model has no cost axis. */
  cost_param_value: string | undefined;
}

export type ValidateGenParamsResult =
  | { ok: true; resolved: ResolvedGenParams }
  | { ok: false; error: string };

/**
 * Validate requested `aspect_ratio` / `size_param_value` / `cost_param_value`
 * against a model's `image_caps`. Closed-enum membership: a present value MUST
 * be one of the caps keys; an absent value falls back to the caps default. A
 * present value on a model with no matching axis is rejected (no silent
 * passthrough).
 */
export function validateGenParamsAgainstCaps(
  caps: ImageCaps,
  input: {
    aspect_ratio?: string | undefined;
    size_param_value?: string | undefined;
    cost_param_value?: string | undefined;
  },
): ValidateGenParamsResult {
  const ratioKeys = new Set(caps.ratio_param.values.map((v) => v.key));
  let aspect_ratio: string;
  if (input.aspect_ratio === undefined) {
    aspect_ratio = caps.ratio_param.default;
  } else if (ratioKeys.has(input.aspect_ratio)) {
    aspect_ratio = input.aspect_ratio;
  } else {
    return {
      ok: false,
      error: `aspect_ratio "${input.aspect_ratio}" is not a valid ${caps.ratio_param.field} for this model (allowed: ${[...ratioKeys].join(', ')})`,
    };
  }

  // ── size_param validation (3-axis model) ──
  let size_param_value: string | undefined;
  if (caps.size_param) {
    const sizeKeys = new Set(caps.size_param.values.map((v) => v.key));
    if (input.size_param_value === undefined) {
      size_param_value = caps.size_param.default;
    } else if (sizeKeys.has(input.size_param_value)) {
      size_param_value = input.size_param_value;
    } else {
      return {
        ok: false,
        error: `size_param_value "${input.size_param_value}" is not a valid resolution tier for this model (allowed: ${[...sizeKeys].join(', ')})`,
      };
    }
  } else if (input.size_param_value !== undefined) {
    return {
      ok: false,
      error: `model has no size parameter but size_param_value "${input.size_param_value}" was provided`,
    };
  }

  let cost_param_value: string | undefined;
  if (caps.cost_param) {
    const costKeys = new Set(caps.cost_param.values.map((v) => v.key));
    if (input.cost_param_value === undefined) {
      cost_param_value = caps.cost_param.default;
    } else if (costKeys.has(input.cost_param_value)) {
      cost_param_value = input.cost_param_value;
    } else {
      return {
        ok: false,
        error: `${caps.cost_param.field} "${input.cost_param_value}" is not valid for this model (allowed: ${[...costKeys].join(', ')})`,
      };
    }
  } else if (input.cost_param_value !== undefined) {
    return {
      ok: false,
      error: `model has no cost parameter but cost_param_value "${input.cost_param_value}" was provided`,
    };
  }

  return { ok: true, resolved: { aspect_ratio, size_param_value, cost_param_value } };
}

/**
 * Position a `cost_param` key inside its ordered `values` list as a 0..1
 * fraction: first value → 0, last → 1. Returns null when the model has no
 * cost axis, the key is unknown, or the axis has a single value (no spread to
 * interpolate over).
 */
function costParamFraction(caps: ImageCaps, costParamValue: string): number | null {
  const axis = caps.cost_param;
  if (!axis || axis.values.length < 2) return null;
  const index = axis.values.findIndex((v) => v.key === costParamValue);
  if (index < 0) return null;
  return index / (axis.values.length - 1);
}

/**
 * Estimate the per-image USD cost from caps pricing.
 *
 * `flat` → exact. `range` → interpolated across the model's ordered
 * `cost_param` tiers when `costParamValue` names one (cheapest tier →
 * `range.min`, priciest → `range.max`, evenly spaced in between), else the
 * midpoint. Returns null when pricing carries neither flat nor range.
 *
 * The tier interpolation exists because a bare midpoint is badly wrong for
 * wide ranges: `openai/gpt-image-2/edit` is seeded at {min 0.005, max 0.401}
 * (`supabase/migrations/20260701000007_seed_image_caps_3axis.sql`), so a
 * `quality: 'low'` generation — what blog-images runs — was being estimated at
 * ~$0.203 instead of ~$0.005, a ~40× overstatement. Callers that know which
 * tier they requested should pass it. Omitting the argument preserves the
 * previous midpoint behaviour exactly (interiors' existing call sites).
 */
export function estimateImageCostFromCaps(
  caps: ImageCaps,
  costParamValue?: string,
): number | null {
  const { pricing } = caps;
  if (pricing.mode === 'flat' && typeof pricing.flat === 'number') return pricing.flat;

  const range = pricing.mode === 'range' && pricing.range ? pricing.range : null;
  if (range) {
    const fraction = costParamValue === undefined ? null : costParamFraction(caps, costParamValue);
    if (fraction !== null) return range.min + (range.max - range.min) * fraction;
    return (range.min + range.max) / 2;
  }

  if (typeof pricing.flat === 'number') return pricing.flat;
  if (pricing.range) {
    const fraction = costParamValue === undefined ? null : costParamFraction(caps, costParamValue);
    if (fraction !== null) {
      return pricing.range.min + (pricing.range.max - pricing.range.min) * fraction;
    }
    return (pricing.range.min + pricing.range.max) / 2;
  }
  return null;
}
